if (!customElements.get('back-in-stock-form')) {
  class BackInStockForm extends HTMLElement {
    constructor() {
      super();
      this.emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.input = this.form.querySelector('#ContactFormSoldout-email');
      this.inputWrap = this.form.querySelector('.back-in-stock-form__input-wrap');
      this.consent = this.form.querySelector('.back-in-stock-form__checkbox');
      this.submitButtons = [...this.form.querySelectorAll('[type="submit"]')];
      this.variantInput = this.form.querySelector('.back-in-stock-form__variant');
      this.errorEl = this.form.querySelector('.back-in-stock-form__error');
      this.errorDescribedById = this.errorEl?.id || '';
      this.successEl = this.form.querySelector('.back-in-stock-form__success');
      this.arrowIcon = this.form.querySelector('.back-in-stock-form__input-icon--arrow');
      this.checkIcon = this.form.querySelector('.back-in-stock-form__input-icon--success');

      this.strings = {
        emailError: this.dataset.emailError || window.backInStockStrings?.emailError || '',
        consentError: this.dataset.consentError || window.backInStockStrings?.consentError || '',
        successInput: this.dataset.successInput || window.backInStockStrings?.successInput || '',
        alreadySubscribed: window.backInStockStrings?.alreadySubscribed || '',
        apiError: window.backInStockStrings?.apiError || '',
      };
      this.defaultEmail = this.input?.value || '';
      this.defaultSuccessMessage = this.successEl?.textContent || '';
      this.autoCloseDelay = 2000;
      this.isSubmitting = false;

      this._onSubmit = this.onSubmit.bind(this);
      this._onInput = this.onInput.bind(this);
      this.form.addEventListener('submit', this._onSubmit);
      this.input?.addEventListener('input', this._onInput);
      this.consent?.addEventListener('change', this._onInput);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this._onSubmit);
      this.input?.removeEventListener('input', this._onInput);
      this.consent?.removeEventListener('change', this._onInput);
      clearTimeout(this._closeTimer);
    }

    onInput() {
      if (this.classList.contains('is-success')) return;
      if (this.inputWrap?.classList.contains('back-in-stock-form__input-wrap--error')) {
        this.clearError();
      }
    }

    showError(message, invalidField = this.input) {
      const msg = message || this.strings.emailError;
      this.setLoading(false);
      this.classList.remove('is-success');
      this.inputWrap?.classList.remove('back-in-stock-form__input-wrap--success');
      this.inputWrap?.classList.add('back-in-stock-form__input-wrap--error');
      this.successEl?.classList.add('hidden');
      if (this.errorEl) {
        this.errorEl.textContent = msg;
        this.errorEl.classList.remove('hidden');
      }
      this.input?.removeAttribute('aria-invalid');
      this.input?.removeAttribute('aria-describedby');
      this.consent?.removeAttribute('aria-invalid');
      this.consent?.removeAttribute('aria-describedby');
      if (invalidField) {
        invalidField.setAttribute('aria-invalid', 'true');
        if (this.errorDescribedById) {
          invalidField.setAttribute('aria-describedby', this.errorDescribedById);
        }
      }
      this.arrowIcon?.classList.remove('hidden');
      this.checkIcon?.classList.add('hidden');
    }

    clearError() {
      this.inputWrap?.classList.remove('back-in-stock-form__input-wrap--error');
      this.errorEl?.classList.add('hidden');
      this.input?.removeAttribute('aria-invalid');
      this.input?.removeAttribute('aria-describedby');
      this.consent?.removeAttribute('aria-invalid');
      this.consent?.removeAttribute('aria-describedby');
    }

    showSuccess(message = this.defaultSuccessMessage) {
      this.classList.add('is-success');
      this.clearError();
      this.inputWrap?.classList.add('back-in-stock-form__input-wrap--success');
      if (this.input) {
        this.input.value = this.strings.successInput;
        this.input.setAttribute('readonly', 'readonly');
      }
      this.arrowIcon?.classList.add('hidden');
      this.checkIcon?.classList.remove('hidden');
      if (this.successEl) {
        this.successEl.textContent = message;
        this.successEl.classList.remove('hidden');
      }
      this.setLoading(false);
      this.scheduleAutoClose();
    }

    /* Auto-dismiss the modal a moment after success, then reset the form so a
       later reopen starts fresh. */
    scheduleAutoClose() {
      clearTimeout(this._closeTimer);
      const modal = this.closest('modal-dialog');
      if (!modal || typeof modal.hide !== 'function') return;
      this._closeTimer = setTimeout(() => {
        modal.hide();
        setTimeout(() => this.reset(), 550);
      }, this.autoCloseDelay);
    }

    reset() {
      this.classList.remove('is-success');
      this.inputWrap?.classList.remove(
        'back-in-stock-form__input-wrap--success',
        'back-in-stock-form__input-wrap--error'
      );
      if (this.input) {
        this.input.removeAttribute('readonly');
        this.input.removeAttribute('aria-invalid');
        this.input.removeAttribute('aria-describedby');
        this.input.value = this.defaultEmail;
      }
      if (this.consent) {
        this.consent.checked = false;
        this.consent.removeAttribute('aria-invalid');
        this.consent.removeAttribute('aria-describedby');
      }
      this.arrowIcon?.classList.remove('hidden');
      this.checkIcon?.classList.add('hidden');
      if (this.successEl) {
        this.successEl.textContent = this.defaultSuccessMessage;
        this.successEl.classList.add('hidden');
      }
      this.errorEl?.classList.add('hidden');
      this.setLoading(false);
    }

    validate() {
      const email = this.input?.value?.trim() || '';
      if (!email || !this.emailPattern.test(email)) {
        this.showError();
        return false;
      }
      if (this.consent && !this.consent.checked) {
        this.showError(this.strings.consentError, this.consent);
        return false;
      }
      this.clearError();
      return true;
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      this.form.setAttribute('aria-busy', String(isLoading));
      this.submitButtons.forEach((button) => {
        button.disabled = isLoading;
        if (button.classList.contains('back-in-stock-form__submit')) {
          button.classList.toggle('loading', isLoading);
        }
      });
    }

    getPayload() {
      return {
        email: this.input?.value?.trim() || '',
        country: this.dataset.country || '',
        language: this.dataset.language || '',
        product_sku: this.variantInput?.dataset.productSku || '',
        variant_id: this.variantInput?.dataset.variantId || '',
      };
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.classList.contains('is-success') || this.isSubmitting) return;
      if (!this.validate()) return;

      const payload = this.getPayload();
      if (!payload.country || !payload.language || !payload.product_sku || !payload.variant_id) {
        this.showError(this.strings.apiError, null);
        return;
      }

      this.setLoading(true);
      const response = await window.ArmadaApi?.post?.('/back-in-stock/subscribe', payload, {
        includeLocale: false,
      });
      const status = response?.ok ? response.data?.status : null;

      if (status === 'already_subscribed') {
        this.showSuccess(this.strings.alreadySubscribed);
        return;
      }
      if (status === 'subscribed') {
        this.showSuccess();
        return;
      }

      this.showError(this.strings.apiError, null);
    }
  }

  customElements.define('back-in-stock-form', BackInStockForm);
}
